import type { CSSProperties } from 'react'
import type { ReportTypeId } from '@/types/dashboard'

export type FeatureTheme = {
  primary: string
  strong: string
  soft: string
  border: string
  surface: string
  glow: string
  tagBackground: string
  tagText: string
  navBackground: string
  navBorder: string
  navText: string
  spinnerBorder: string
  spinnerTrack: string
}

type ThemeStyle = CSSProperties & Record<`--${string}`, string>

export type FeatureThemeKey =
  | 'practiceIntel'
  | 'advisoryIntel'
  | 'complianceIntel'
  | 'pracPlanner'
  | 'pracIntel'

export type ProductCopy = {
  productName: string
  itemSingular: string
  itemPlural: string
  linkedAssetLabel?: string
  screenTitle?: string
}

export type ProductBranding = {
  featureKey: FeatureThemeKey
  theme: FeatureTheme
  copy: ProductCopy
  navSectionKey?: string
}

function hexToRgb(hex: string) {
  const normalized = hex.replace('#', '')
  const expanded = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized

  const value = Number.parseInt(expanded, 16)
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

function withAlpha(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function mix(hex: string, target: string, weight: number) {
  const sourceRgb = hexToRgb(hex)
  const targetRgb = hexToRgb(target)
  const blend = (source: number, destination: number) => Math.round(source + (destination - source) * weight)
  return `rgb(${blend(sourceRgb.r, targetRgb.r)}, ${blend(sourceRgb.g, targetRgb.g)}, ${blend(sourceRgb.b, targetRgb.b)})`
}

function createFeatureTheme({
  primary,
  strong,
  softAlpha = 0.14,
  borderAlpha = 0.24,
  glowAlpha = 0.18,
  surface = withAlpha(primary, 0.06),
  tagText,
}: {
  primary: string
  strong: string
  softAlpha?: number
  borderAlpha?: number
  glowAlpha?: number
  surface?: string
  tagText?: string
}): FeatureTheme {
  return {
    primary,
    strong,
    soft: withAlpha(primary, softAlpha),
    border: withAlpha(primary, borderAlpha),
    surface,
    glow: withAlpha(primary, glowAlpha),
    tagBackground: withAlpha(primary, softAlpha),
    tagText: tagText ?? strong,
    navBackground: primary,
    navBorder: strong,
    navText: '#ffffff',
    spinnerBorder: primary,
    spinnerTrack: withAlpha(primary, glowAlpha),
  }
}

const practiceBaseTheme = createFeatureTheme({
  primary: '#00c8b9',
  strong: '#009d92',
  surface: 'rgba(241, 255, 252, 0.98)',
  tagText: '#007b73',
})

export const productThemes = {
  practiceIntel: createFeatureTheme({
    primary: practiceBaseTheme.primary,
    strong: practiceBaseTheme.strong,
    surface: 'rgba(240, 255, 252, 0.96)',
    tagText: practiceBaseTheme.tagText,
  }),
  advisoryIntel: createFeatureTheme({
    primary: '#33bce1',
    strong: '#1787a6',
    surface: 'rgba(241, 251, 255, 0.98)',
    tagText: '#156b83',
  }),
  complianceIntel: createFeatureTheme({
    primary: '#00a591',
    strong: '#0a7d6f',
    softAlpha: 0.12,
    glowAlpha: 0.16,
    surface: 'rgba(241, 253, 250, 0.96)',
    tagText: '#0e685d',
  }),
  pracPlanner: practiceBaseTheme,
  pracIntel: practiceBaseTheme,
} satisfies Record<FeatureThemeKey, FeatureTheme>

export const featureThemeClassNames: Record<FeatureThemeKey, string> = {
  practiceIntel: 'feature-theme--practice-intel',
  advisoryIntel: 'feature-theme--advisory-intel',
  complianceIntel: 'feature-theme--compliance-intel',
  pracPlanner: 'feature-theme--prac-planner',
  pracIntel: 'feature-theme--prac-intel',
}

export const productBranding: Record<FeatureThemeKey, ProductBranding> = {
  practiceIntel: {
    featureKey: 'practiceIntel',
    theme: productThemes.practiceIntel,
    navSectionKey: 'practice',
    copy: {
      productName: 'Practice Intel',
      itemSingular: 'dashboard',
      itemPlural: 'dashboards',
      linkedAssetLabel: 'Dashboard linked',
      screenTitle: 'Practice Intel',
    },
  },
  advisoryIntel: {
    featureKey: 'advisoryIntel',
    theme: productThemes.advisoryIntel,
    navSectionKey: 'advisory',
    copy: {
      productName: 'Advisory Intel',
      itemSingular: 'dashboard',
      itemPlural: 'dashboards',
      linkedAssetLabel: 'Dashboard linked',
      screenTitle: 'Advisory Intel',
    },
  },
  complianceIntel: {
    featureKey: 'complianceIntel',
    theme: productThemes.complianceIntel,
    navSectionKey: 'compliance',
    copy: {
      productName: 'CompVerify',
      itemSingular: 'job',
      itemPlural: 'jobs',
      screenTitle: 'CompVerify',
    },
  },
  pracPlanner: {
    featureKey: 'pracPlanner',
    theme: productThemes.pracPlanner,
    navSectionKey: 'practice',
    copy: {
      productName: 'PracPlanner',
      itemSingular: 'planner',
      itemPlural: 'planners',
      screenTitle: 'PracPlanner',
    },
  },
  pracIntel: {
    featureKey: 'pracIntel',
    theme: productThemes.pracIntel,
    navSectionKey: 'practice',
    copy: {
      productName: 'PracIntel',
      itemSingular: 'workspace',
      itemPlural: 'workspaces',
      screenTitle: 'PracIntel',
    },
  },
}

export const dashboardBranding: Record<ReportTypeId, ProductBranding> = {
  1: productBranding.practiceIntel,
  2: productBranding.advisoryIntel,
}

export function getFeatureTheme(featureKey: FeatureThemeKey): FeatureTheme {
  return productThemes[featureKey]
}

export function getFeatureThemeClassName(featureKey: FeatureThemeKey) {
  return featureThemeClassNames[featureKey]
}

export function getFeatureThemeStyle(theme: FeatureTheme): ThemeStyle {
  const primaryRgb = hexToRgb(theme.primary)
  return {
    '--feature-primary': theme.primary,
    '--feature-primary-strong': theme.strong,
    '--feature-primary-soft': theme.soft,
    '--feature-border': theme.border,
    '--feature-surface': theme.surface,
    '--feature-glow': theme.glow,
    '--feature-tag-background': theme.tagBackground,
    '--feature-tag-text': theme.tagText,
    '--feature-nav-background': theme.navBackground,
    '--feature-nav-border': theme.navBorder,
    '--feature-nav-text': theme.navText,
    '--feature-spinner-border': theme.spinnerBorder,
    '--feature-spinner-track': theme.spinnerTrack,
    '--feature-primary-rgb': primaryRgb.r + ', ' + primaryRgb.g + ', ' + primaryRgb.b,
    '--feature-primary-muted': mix(theme.primary, '#ffffff', 0.7),
    '--feature-primary-deep': mix(theme.primary, '#000000', 0.18),
  }
}
